import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Check } from 'lucide-react';
import { api, resolveAsset } from '../api/client';
import VariantPicker from '../components/VariantPicker';
import BackButton from '../components/BackButton';
import AddedToCartSheet from '../components/AddedToCartSheet';
import { getReviews, getStats } from '../data/reviews';
import { trackViewContent, trackAddToCart, getFbCookies, isPixelSuppressed } from '../lib/pixel';
import { trackEvent } from '../lib/track';
import { getAfMode } from '../lib/afMode';
import { useExperiment } from '../state/experiment';
import { useCart } from '../state/cart';
import { useT, useLang, useLocalField } from '../i18n';

export default function TemplateDetail() {
  const t = useT();
  const { lang } = useLang();
  const lf = useLocalField();
  const REVIEWS = useMemo(() => getReviews(lang), [lang]);
  const STATS = useMemo(() => getStats(lang), [lang]);
  const { slug } = useParams();
  const { payFirst } = useExperiment();
  const { addSlot } = useCart();
  const [template, setTemplate] = useState(null);
  const [selectedSku, setSelectedSku] = useState('');
  const [loading, setLoading] = useState(true);
  // Pay-first variant: how many times this book has been added to the cart in
  // this view — drives the "added ✓ / go to cart" affordance.
  const [addedCount, setAddedCount] = useState(0);
  // Post-add decision sheet: pay now vs. keep adding books.
  const [showAddedSheet, setShowAddedSheet] = useState(false);
  // ?af=pdf funnel (see lib/afMode): the digital (PDF) variant is fixed — no
  // book options shown here either, matching the create page.
  const [pdfOnly, setPdfOnly] = useState(false);

  useEffect(() => {
    api.get(`/api/templates/${slug}`)
      .then((tp) => {
        setTemplate(tp);
        const digital = getAfMode() === 'pdf'
          ? tp.variants?.find((v) => v.isActive && v.type === 'digital')
          : null;
        const initial = digital || tp.variants?.find(v => v.isActive);
        if (digital) setPdfOnly(true);
        if (initial) setSelectedSku(initial.sku);
        trackViewContent({
          id: tp.slug,
          name: tp.nameAr,
          value: initial ? initial.priceCents / 100 : undefined,
        });
        // First-party "most-viewed stories" analytics.
        trackEvent('template_view', { slug: tp.slug, name: tp.nameAr });
      })
      .catch(() => setTemplate(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="loader"><div className="loader-spinner" /></div>;
  if (!template) return (
    <div className="error-card">
      <h2>{t.templateNotFound}</h2>
      <Link to="/" className="submit-btn">{t.backToHome}</Link>
    </div>
  );

  const selectedVariant = template.variants?.find(v => v.sku === selectedSku);

  // Pay-first: add this book (template + chosen variant) to the cart as a slot,
  // with no story yet. The story is created after payment, from the order. Price
  // shown here is display-only; checkout re-prices authoritatively server-side.
  const handleAddToCart = () => {
    if (!selectedVariant) return;
    addSlot({
      templateSlug: template.slug,
      templateId: template._id,
      nameAr: template.nameAr,
      nameHe: template.nameHe,
      coverImageUrl: template.coverImageUrl || null,
      variantSku: selectedVariant.sku,
      variantType: selectedVariant.type,
      priceCents: selectedVariant.priceCents,
    });
    setAddedCount((n) => n + 1);
    setShowAddedSheet(true);
    trackEvent('payfirst_add_to_cart', { slug: template.slug, name: template.nameAr });
    // Meta AddToCart at the true add-to-cart moment — the event the AddToCart-
    // optimized campaign bids on. Browser pixel + a server CAPI copy (the beacon
    // below) share one eventId so Meta dedupes them into a single conversion;
    // the server copy survives ad blockers/ITP. Skipped entirely while the
    // pixel is suppressed (signed-in staff).
    if (!isPixelSuppressed()) {
      const eventId = `atc-${window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`}`;
      trackAddToCart({
        id: template.slug,
        name: template.nameAr,
        value: selectedVariant.priceCents / 100,
        eventId,
      });
      api.post('/api/track/meta-atc', {
        eventId,
        templateSlug: template.slug,
        variantSku: selectedVariant.sku,
        eventSourceUrl: window.location.href,
        ...getFbCookies(),
      }).catch(() => { /* fire-and-forget — never disturb the add-to-cart UX */ });
    }
  };

  return (
    <div className="template-detail">
      <BackButton />
      <div className="template-detail-grid">
        <div className="template-detail-media">
          {template.coverImageUrl ? (
            <img src={resolveAsset(template.coverImageUrl)} alt={lf(template, 'nameAr')} className="template-detail-cover" />
          ) : (
            <div className="template-detail-cover template-card-placeholder" />
          )}
          {template.sampleImages?.length > 0 && (
            <div className="template-samples">
              {template.sampleImages.map((url, i) => (
                <img key={i} src={resolveAsset(url)} alt="" className="template-sample-img" />
              ))}
            </div>
          )}
        </div>

        <div className="template-detail-info">
          <h1 className="template-detail-title">{lf(template, 'nameAr')}</h1>
          {lf(template, 'taglineAr') && <p className="template-detail-tagline">{lf(template, 'taglineAr')}</p>}
          <p className="template-detail-desc">{lf(template, 'descriptionAr')}</p>

          {template.ageBands?.length > 0 && (
            <div className="template-detail-meta">
              <span className="meta-label">{t.ageBandLabel}:</span>
              <span>{template.ageBands.join(' / ')}</span>
            </div>
          )}

          {!pdfOnly && (
            <VariantPicker
              variants={template.variants}
              selected={selectedSku}
              onSelect={setSelectedSku}
            />
          )}

          {selectedVariant && (
            <p className="template-detail-price">
              {pdfOnly ? `${t.digital} — ` : ''}
              {!pdfOnly && selectedVariant.compareAtCents > selectedVariant.priceCents && (
                <span className="price-was">{(selectedVariant.compareAtCents / 100).toFixed(0)}{t.currency}</span>
              )}
              {((selectedVariant.priceCents + (pdfOnly ? (template.afPdfMarginCents || 0) : 0)) / 100).toFixed(0)}{t.currency}
            </p>
          )}

          {payFirst ? (
            <div className="payfirst-cta">
              <button
                type="button"
                className="submit-btn template-detail-cta"
                disabled={!selectedVariant}
                onClick={handleAddToCart}
              >
                {addedCount > 0 ? t.payFirstAddAnother : t.payFirstAddToCart}
              </button>
              {/* Expectation-setting for the pay-first flow: adding = buying;
                  the personalization happens after payment. */}
              <p className="payfirst-cta-note">{t.payFirstCtaNote}</p>
              {addedCount > 0 && (
                <div className="payfirst-added">
                  <span className="payfirst-added-note">
                    <Check size={15} aria-hidden="true" /> {t.payFirstAddedNote}
                  </span>
                  <Link to="/cart" className="ghost-btn payfirst-go-cart">
                    {t.payFirstGoToCart} ({addedCount})
                  </Link>
                </div>
              )}
            </div>
          ) : (
            <Link
              to={`/create/${template.slug}${selectedSku ? `?variant=${encodeURIComponent(selectedSku)}` : ''}`}
              className="submit-btn template-detail-cta"
            >
              {t.createYourStory}
            </Link>
          )}

          <div className="trust-mini">
            <div className="trust-mini-stars">
              {Array.from({ length: 5 }, (_, i) => (
                <Star key={i} size={14} fill="#f59e0b" color="#f59e0b" />
              ))}
              <span>{STATS.avgRating} ({STATS.reviewCount} {t.templateReviewCount})</span>
            </div>
            <p className="trust-mini-quote">
              &ldquo;{REVIEWS.find(r => r.template === lf(template, 'nameAr'))?.text || REVIEWS[0].text}&rdquo;
              <span className="trust-mini-author"> — {REVIEWS.find(r => r.template === lf(template, 'nameAr'))?.name || REVIEWS[0].name}</span>
            </p>
          </div>
        </div>
      </div>
      {showAddedSheet && <AddedToCartSheet onClose={() => setShowAddedSheet(false)} />}
    </div>
  );
}
